import React, { useEffect, useMemo, useState } from 'react';
import AdminHeader from '../Layout/AdminHeader';
import AdminSidebar from '../Layout/AdminSidebar';
import { getChatPermissions, saveChatPermissions } from '../../services/chat';
import '../Dashboard/Dashboard.css';
import './ChatPermissionsEditor.css';

const ROLE_LABELS = {
  Recruiter: 'Recruiter',
  BoothAdmin: 'Booth Admin',
  Support: 'Support',
  Interpreter: 'Interpreter',
  GlobalSupport: 'Global Support',
  GlobalInterpreter: 'Global Interpreter'
};

const ROLE_PLURALS = {
  Recruiter: 'Recruiters',
  BoothAdmin: 'Booth Admins',
  Support: 'Support staff',
  Interpreter: 'Interpreters',
  GlobalSupport: 'Global Support',
  GlobalInterpreter: 'Global Interpreters'
};

const SCOPE_LABELS = {
  none: 'Not allowed',
  booth: 'Same booth',
  event: 'Same event'
};

const roleLabel = (role) => ROLE_LABELS[role] || role;
const rolePlural = (role) => ROLE_PLURALS[role] || role;

const copyMatrix = (matrix) => Object.fromEntries(
  Object.entries(matrix || {}).map(([fromRole, row]) => [fromRole, { ...row }])
);

const countDifferences = (roles, a, b) => roles.reduce(
  (total, fromRole) => total + roles.filter((toRole) => a?.[fromRole]?.[toRole] !== b?.[fromRole]?.[toRole]).length,
  0
);

/** One plain-language sentence per role, e.g. "Recruiters can start a chat with: Support (same booth)". */
const describeRole = (roles, matrix, fromRole) => {
  const targets = roles
    .filter((toRole) => matrix[fromRole]?.[toRole] && matrix[fromRole][toRole] !== 'none')
    .map((toRole) => `${rolePlural(toRole)} (${SCOPE_LABELS[matrix[fromRole][toRole]].toLowerCase()})`);
  return targets.length
    ? `${rolePlural(fromRole)} can start a chat with: ${targets.join(', ')}.`
    : `${rolePlural(fromRole)} cannot start a chat with anyone.`;
};

export default function ChatPermissionsEditor() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  // Messages stay until dismissed (WCAG 2.2.1); errors go to the alert region
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await getChatPermissions();
      setConfig(data);
      setForm(copyMatrix(data.permissions));
    } catch (err) {
      setLoadError(err?.message || 'Could not load the Team Chat permissions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const roles = config?.roles || [];
  const boothRoles = useMemo(() => new Set(config?.boothRoles || []), [config]);
  const scopesFor = (fromRole, toRole) => (
    boothRoles.has(fromRole) && boothRoles.has(toRole)
      ? config.scopes
      : config.scopes.filter((scope) => scope !== 'booth')
  );

  const unsavedCount = config && form ? countDifferences(roles, form, config.permissions) : 0;
  const changedFromDefaultCount = config && form ? countDifferences(roles, form, config.defaults) : 0;

  const setCell = (fromRole, toRole, scope) => {
    setForm((prev) => ({ ...prev, [fromRole]: { ...prev[fromRole], [toRole]: scope } }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setStatus('');
    try {
      const data = await saveChatPermissions(form);
      setConfig(data);
      setForm(copyMatrix(data.permissions));
      const changed = countDifferences(data.roles, data.permissions, data.defaults);
      setStatus(changed === 0
        ? 'Saved. Team Chat now uses the system defaults.'
        : `Saved. ${changed} ${changed === 1 ? 'rule differs' : 'rules differ'} from the system defaults. People see the change the next time their Team Chat loads.`);
    } catch (err) {
      setError(err?.message || 'Could not save the Team Chat permissions.');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setForm(copyMatrix(config.permissions));
    setError('');
    setStatus('Unsaved changes discarded.');
  };

  const handleLoadDefaults = () => {
    setForm(copyMatrix(config.defaults));
    setError('');
    setStatus('System defaults filled in. Select Save changes to apply them.');
  };

  const busy = loading || saving;

  return (
    <div className="dashboard">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <AdminHeader />
      <div className="dashboard-layout">
        <AdminSidebar active="chat-permissions" />
        <main id="main-content" className="dashboard-main" tabIndex={-1} aria-label="main content">
          <div className="dashboard-content chat-perms">
            <h1>Team Chat Permissions</h1>

            <div className="chat-perms-intro">
              <p>
                Choose which roles can start a Team Chat with which other roles. Read each row as
                &quot;this role can start a chat with&quot; the role in the column.
              </p>
              <ul>
                <li><strong>Same booth</strong>: both people are assigned to the same booth.</li>
                <li><strong>Same event</strong>: both people work the same event.</li>
                <li><strong>Not allowed</strong>: the person does not appear in the list.</li>
              </ul>
              <p>
                Once a chat is open, both people can keep replying while either direction is allowed.
                The default under each choice is how the system works when nothing has been changed.
              </p>
            </div>

            <div role="status" aria-live="polite" className="chat-perms-live">
              {loading && 'Loading Team Chat permissions…'}
              {!loading && status && (
                <div className="chat-perms-message">
                  <span>{status}</span>
                  <button type="button" className="chat-perms-dismiss" onClick={() => setStatus('')}>
                    Dismiss<span className="sr-only"> message</span>
                  </button>
                </div>
              )}
            </div>
            <div role="alert" className="chat-perms-live">
              {(error || loadError) && (
                <div className="chat-perms-message chat-perms-message--error">
                  <span>{error || loadError}</span>
                  {loadError ? (
                    <button type="button" className="chat-perms-dismiss" onClick={load}>Try again</button>
                  ) : (
                    <button type="button" className="chat-perms-dismiss" onClick={() => setError('')}>
                      Dismiss<span className="sr-only"> error</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            {config && form && (
              <>
                <p className="chat-perms-counts">
                  {changedFromDefaultCount === 0
                    ? 'All rules match the system defaults.'
                    : `${changedFromDefaultCount} ${changedFromDefaultCount === 1 ? 'rule differs' : 'rules differ'} from the system defaults.`}
                  {unsavedCount > 0 && (
                    <strong> {unsavedCount} unsaved {unsavedCount === 1 ? 'change' : 'changes'}.</strong>
                  )}
                </p>

                <div className="chat-perms-scroll">
                  <table className="chat-perms-table">
                    <caption id="chat-perms-caption">Who can start a Team Chat with whom</caption>
                    <thead>
                      <tr>
                        <th scope="col">Starts the chat</th>
                        {roles.map((toRole) => (
                          <th scope="col" key={toRole}>{roleLabel(toRole)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {roles.map((fromRole) => (
                        <tr key={fromRole}>
                          <th scope="row">{roleLabel(fromRole)}</th>
                          {roles.map((toRole) => {
                            const id = `chat-perm-${fromRole}-${toRole}`;
                            const value = form[fromRole][toRole];
                            const defaultValue = config.defaults[fromRole][toRole];
                            const isChanged = value !== defaultValue;
                            return (
                              <td key={toRole} className={isChanged ? 'chat-perms-cell--changed' : undefined}>
                                <label htmlFor={id} className="sr-only">
                                  {`${roleLabel(fromRole)} can start a chat with ${roleLabel(toRole)}`}
                                </label>
                                <select
                                  id={id}
                                  className="chat-perms-select"
                                  value={value}
                                  onChange={(e) => setCell(fromRole, toRole, e.target.value)}
                                  disabled={busy}
                                  aria-describedby={`${id}-default`}
                                >
                                  {scopesFor(fromRole, toRole).map((scope) => (
                                    <option key={scope} value={scope}>{SCOPE_LABELS[scope]}</option>
                                  ))}
                                </select>
                                <span id={`${id}-default`} className="chat-perms-default">
                                  {isChanged
                                    ? <><strong>Changed.</strong> Default: {SCOPE_LABELS[defaultValue]}</>
                                    : <>Default: {SCOPE_LABELS[defaultValue]}</>}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="chat-perms-actions">
                  <button
                    type="button"
                    className="dashboard-button"
                    onClick={handleSave}
                    disabled={busy || unsavedCount === 0}
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                  <button
                    type="button"
                    className="chat-perms-secondary"
                    onClick={handleDiscard}
                    disabled={busy || unsavedCount === 0}
                  >
                    Discard unsaved changes
                  </button>
                  <button
                    type="button"
                    className="chat-perms-secondary"
                    onClick={handleLoadDefaults}
                    disabled={busy || changedFromDefaultCount === 0}
                  >
                    Reset to system defaults
                  </button>
                </div>

                <h2 className="chat-perms-summary-heading">In plain words</h2>
                <p className="chat-perms-note">This summary follows the choices above, including unsaved ones.</p>
                <ul className="chat-perms-summary">
                  {roles.map((fromRole) => (
                    <li key={fromRole}>{describeRole(roles, form, fromRole)}</li>
                  ))}
                </ul>
                <p className="chat-perms-note">
                  Admins, event admins, SuperAdmins and job seekers do not use Team Chat. The Event Broadcast stays
                  limited to Global Support.
                </p>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
